import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { resetDeactivateVirtualCardContent } from 'src/app/DeactivateVirtualCardContent/redux/deactivateVirtualCardContent.actions'
import {
  selectPuttingVirtualCardCancelFromState,
  selectTopLevelErrorFromState,
} from 'src/app/DeactivateVirtualCardContent/redux/deactivateVirtualCardContent.selectors'
import { selectReceiptAttachmentModelsList } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import { createSaveSelectedVirtualCardIdsAction } from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.actions'
import CancelOrDeactivateVirtualCardsPopup from 'src/components/CancelOrDeactivateVirtualCardsPopup/CancelOrDeactivateVirtualCardsPopup'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { useModalRoute } from 'src/navigation/modalParams'
import { useCancelVirtualCard } from 'src/queries/virtualCards.queries'
import { closeOrRedirectAfterVirtualCardCancel } from 'src/redux/actions/data/virtualCards.actions'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import { toDollars } from 'src/utils/currency.utils'

export interface DeactivateVirtualCardContentProps {
  virtualCard: VirtualCardListModel | undefined
}

const DeactivateVirtualCardContent: React.FC<
  DeactivateVirtualCardContentProps
> = ({ virtualCard }) => {
  const translate = useTranslate()
  const modal = useModalRoute()

  const puttingVirtualCardCancel = useSelector(
    selectPuttingVirtualCardCancelFromState,
  )
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const { analytics } = useAnalyticsContext()
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)

  const dispatch = useFlyoutDispatch()
  const cancelVirtualCard = useCancelVirtualCard()

  useEffect(() => {
    // Clear loading/error left by a previously-opened (abandoned) card so this
    // freshly-opened modal starts clean. Mount-once: `useFlyoutDispatch` returns
    // a fresh function each render, so depending on it would reset every render
    // and wipe the in-flight loading flag the moment it is set.
    dispatch(resetDeactivateVirtualCardContent())
  }, [])

  return (
    <>
      {virtualCard !== undefined && !virtualCard.isInactive ? (
        <CancelOrDeactivateVirtualCardsPopup
          ariaLabel={
            virtualCard.isPending
              ? translate('Cancel Request')
              : translate('Deactivate')
          }
          title={
            virtualCard.isPending
              ? translate('Cancel Request')
              : translate('Deactivate')
          }
          subtitle={
            virtualCard.isPending
              ? translate('Cancel new card request for ${virtualCardName}?', {
                  virtualCardName: (
                    <span>{virtualCard?.truncatedDisplayName}</span>
                  ),
                })
              : translate('Deactivate the virtual card ${virtualCardName}?', {
                  virtualCardName: (
                    <span>{virtualCard?.truncatedDisplayName}</span>
                  ),
                })
          }
          actionButtonLabel={
            virtualCard.isPending
              ? translate('Yes, Cancel Request')
              : translate('Deactivate')
          }
          actionButtonLoading={puttingVirtualCardCancel}
          actionButtonOnClick={async (): Promise<void> => {
            if (
              selectedVirtualCardIdsFromRedux !== undefined &&
              selectedVirtualCardIdsFromRedux.length > 0
            ) {
              const selectedVirtualCardIds =
                selectedVirtualCardIdsFromRedux.filter(
                  (item) => item !== virtualCard.id,
                )
              dispatch(
                createSaveSelectedVirtualCardIdsAction(selectedVirtualCardIds),
              )
            }
            const eventName = virtualCard.isPending
              ? 'cancel vcn request'
              : 'deactivate card modal - deactivate'
            const properties = virtualCard.isPending
              ? {
                  type_of_card: virtualCard.cardTypeLabel.props.source,
                  card_name: virtualCard.displayName,
                  credit_limit: toDollars(virtualCard.balanceCents),
                  active_until:
                    virtualCard.activeUntilTimezonedMoment.format('YYYY-MM-DD'),
                  add_attachments:
                    receiptAttachments !== undefined &&
                    receiptAttachments.length > 0,
                  add_notes: virtualCard.notes !== undefined,
                }
              : {
                  bulk: false,
                  number_of_cards: 1,
                }
            analytics?.track(eventName, properties)

            try {
              const { virtualCard: cancelledVirtualCard } =
                await cancelVirtualCard.mutateAsync({ id: virtualCard.id })
              dispatch(
                closeOrRedirectAfterVirtualCardCancel(
                  cancelledVirtualCard,
                  virtualCard.isPending,
                  modal.close,
                ),
              )
            } catch {
              // error surfaced via topLevelError below; keep the modal open
            }
          }}
          cancelButtonLabel={
            virtualCard.isPending
              ? translate('No, Keep Request')
              : translate('Cancel')
          }
          cancelButtonOnClick={(): void => {
            modal.close()
            analytics?.track(
              virtualCard.isPending
                ? 'cancel vcn request - keep request'
                : 'deactivate card modal - cancel',
              {
                bulk: false,
                number_of_cards: 1,
              },
            )
          }}
          topLevelError={topLevelError}
        />
      ) : (
        <BaseModal
          isOpen
          onClose={modal.close}
          aria-label={translate('Card not found')}
          showCloseButton
        >
          <NotFoundPopupContent />
        </BaseModal>
      )}
    </>
  )
}

export default DeactivateVirtualCardContent
